import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select } from "../../components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from "../../components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "../../components/ui/dropdown-menu";
import {
    Activity,
    Undo2,
    ChevronLeft,
    ChevronRight,
    RefreshCw,
    Filter,
    AlertCircle,
    Trash2,
    Users,
    Shuffle,
    UserCheck,
    Edit,
    Download,
    Upload
} from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { fetchManagementActions, exportManagementActions, undoManagementAction } from "../../services/crmServices/managementActionService";
import { useAuth } from "../../hooks/useAuth";

const ACTION_TYPE_LABELS = {
    bulk_assign: "Bulk Assign",
    individual_assign: "Individual Assign",
    shuffle: "Shuffle",
    bulk_update: "Bulk Update",
    bulk_delete: "Bulk Delete",
    delete_lead: "Delete Lead",
    bulk_import: "Bulk Import"
};

const ACTION_TYPE_COLORS = {
    bulk_assign: "bg-blue-100 text-blue-800",
    individual_assign: "bg-indigo-100 text-indigo-800",
    shuffle: "bg-purple-100 text-purple-800",
    bulk_update: "bg-amber-100 text-amber-800",
    bulk_delete: "bg-red-100 text-red-800",
    delete_lead: "bg-red-100 text-red-800",
    bulk_import: "bg-green-100 text-green-800"
};

const ACTION_ICONS = {
    bulk_assign: Users,
    individual_assign: UserCheck,
    shuffle: Shuffle,
    bulk_update: Edit,
    bulk_delete: Trash2,
    delete_lead: Trash2,
    bulk_import: Upload
};

const UNDOABLE_TYPES = ["bulk_assign", "individual_assign", "shuffle", "bulk_update"];

export function ManagementActions() {
    const { user } = useAuth();
    const [actions, setActions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [undoingId, setUndoingId] = useState(null);
    const [confirmUndo, setConfirmUndo] = useState(null);
    const [exporting, setExporting] = useState(false);

    // Filters
    const [filterType, setFilterType] = useState("all");
    const [filterDateFrom, setFilterDateFrom] = useState("");
    const [filterDateTo, setFilterDateTo] = useState("");
    const [filterShowUndone, setFilterShowUndone] = useState("all");

    const LIMIT = 25;

    const loadActions = useCallback(async (pageNum = 1) => {
        setLoading(true);
        try {
            const params = {
                page: pageNum,
                limit: LIMIT
            };
            if (filterType && filterType !== "all") params.actionType = filterType;
            if (filterDateFrom) params.dateFrom = filterDateFrom;
            if (filterDateTo) params.dateTo = filterDateTo;
            if (filterShowUndone === "undone") params.isUndone = true;
            if (filterShowUndone === "active") params.isUndone = false;

            const response = await fetchManagementActions(params);
            if (response?.data) {
                setActions(response.data);
                setTotal(response.total || 0);
                setTotalPages(response.totalPages || 1);
                setPage(response.page || 1);
            }
        } catch (error) {
            console.error("Error loading management actions:", error);
            toast.error("Failed to load management actions");
        } finally {
            setLoading(false);
        }
    }, [filterType, filterDateFrom, filterDateTo, filterShowUndone]);

    useEffect(() => {
        if (user?.position?.role !== "admin") {
            toast.error("Access denied. Admin privileges required.");
            return;
        }
        loadActions(1);
    }, [user?.position?.role, loadActions]);

    const handleUndo = async (action) => {
        setConfirmUndo(null);
        setUndoingId(action._id);
        try {
            const response = await undoManagementAction(action._id);
            if (response?.status === 200) {
                toast.success(response.message || `Successfully reverted ${action.affectedLeadCount} lead(s)`);
                loadActions(page);
            }
        } catch (error) {
            const msg = error?.response?.data?.message || "Failed to undo action";
            toast.error(msg);
        } finally {
            setUndoingId(null);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        const d = new Date(dateStr);
        return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    };

    const handleExport = async (format) => {
        setExporting(true);
        try {
            const params = {};
            if (filterType && filterType !== "all") params.actionType = filterType;
            if (filterDateFrom) params.dateFrom = filterDateFrom;
            if (filterDateTo) params.dateTo = filterDateTo;
            if (filterShowUndone === "undone") params.isUndone = true;
            if (filterShowUndone === "active") params.isUndone = false;

            const response = await exportManagementActions(params);

            if (!response?.data || response.data.length === 0) {
                toast.warning("No data to export with the current filters");
                return;
            }

            if (response.capped) {
                toast.info(`Export limited to ${response.maxExport.toLocaleString()} of ${response.total.toLocaleString()} records. Narrow your filters for a complete export.`);
            }

            const rows = response.data.map((action) => ({
                "Type": ACTION_TYPE_LABELS[action.actionType] || action.actionType,
                "Description": action.description || "",
                "Leads Affected": action.affectedLeadCount || 0,
                "Performed By": action.performedBy
                    ? `${action.performedBy.firstName || ""} ${action.performedBy.lastName || ""}`.trim()
                    : "Unknown",
                "Date": action.createdAt ? new Date(action.createdAt).toLocaleString() : "",
                "Status": action.isUndone ? "Undone" : (UNDOABLE_TYPES.includes(action.actionType) ? "Active" : "Permanent"),
                "Undone By": action.undoneBy
                    ? `${action.undoneBy.firstName || ""} ${action.undoneBy.lastName || ""}`.trim()
                    : "",
                "Undone At": action.undoneAt ? new Date(action.undoneAt).toLocaleString() : ""
            }));

            const ws = XLSX.utils.json_to_sheet(rows);

            const colWidths = [
                { wch: 18 }, { wch: 50 }, { wch: 14 }, { wch: 22 },
                { wch: 20 }, { wch: 12 }, { wch: 22 }, { wch: 20 }
            ];
            ws["!cols"] = colWidths;

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Management Actions");

            const dateSuffix = new Date().toISOString().slice(0, 10);
            const filterSuffix = filterType && filterType !== "all" ? `_${filterType}` : "";
            const filename = `management_actions${filterSuffix}_${dateSuffix}`;

            if (format === "csv") {
                XLSX.writeFile(wb, `${filename}.csv`, { bookType: "csv" });
            } else {
                XLSX.writeFile(wb, `${filename}.xlsx`, { bookType: "xlsx" });
            }

            toast.success(`Exported ${rows.length.toLocaleString()} record(s) as ${format.toUpperCase()}`);
        } catch (error) {
            console.error("Export error:", error);
            toast.error("Failed to export data. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    const getPerformerName = (action) => {
        const pb = action.performedBy;
        if (!pb) return "Unknown";
        return `${pb.firstName || ""} ${pb.lastName || ""}`.trim() || "Unknown";
    };

    const getUndoerName = (action) => {
        const ub = action.undoneBy;
        if (!ub) return "—";
        return `${ub.firstName || ""} ${ub.lastName || ""}`.trim() || "Unknown";
    };

    if (user?.position?.role !== "admin") {
        return (
            <div className="container mx-auto px-6 py-8">
                <Card>
                    <CardContent className="flex items-center gap-3 py-8 text-red-600">
                        <AlertCircle className="h-6 w-6" />
                        <span>Access denied. Admin privileges required.</span>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-6 py-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Activity className="h-7 w-7 text-blue-600" />
                        Management Actions
                    </h1>
                    <p className="text-gray-500 mt-1 text-sm">
                        Audit log of actions performed by desk managers, floor managers, general managers and admins.
                        Reversible actions can be undone.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={exporting || loading}
                                className="flex items-center gap-2"
                            >
                                {exporting ? (
                                    <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Download className="h-4 w-4" />
                                )}
                                {exporting ? "Exporting..." : "Export"}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleExport("xlsx")}>
                                Export as Excel (.xlsx)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleExport("csv")}>
                                Export as CSV (.csv)
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadActions(page)}
                        disabled={loading}
                        className="flex items-center gap-2"
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <Card className="mb-6">
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Filter className="h-4 w-4" />
                        Filters
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="text-xs font-medium text-gray-600 mb-1 block">Action Type</label>
                            <Select
                                value={filterType}
                                onChange={(v) => { setFilterType(v); setPage(1); }}
                                placeholder="All types"
                                options={[
                                    { label: "All types", value: "all" },
                                    { label: "Bulk Assign", value: "bulk_assign" },
                                    { label: "Individual Assign", value: "individual_assign" },
                                    { label: "Shuffle", value: "shuffle" },
                                    { label: "Bulk Update", value: "bulk_update" },
                                    { label: "Bulk Delete", value: "bulk_delete" },
                                    { label: "Delete Lead", value: "delete_lead" },
                                    { label: "Bulk Import", value: "bulk_import" }
                                ]}
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-600 mb-1 block">Date From</label>
                            <Input
                                type="date"
                                value={filterDateFrom}
                                onChange={(e) => { setFilterDateFrom(e.target.value); setPage(1); }}
                                className="h-9 text-sm"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-600 mb-1 block">Date To</label>
                            <Input
                                type="date"
                                value={filterDateTo}
                                onChange={(e) => { setFilterDateTo(e.target.value); setPage(1); }}
                                className="h-9 text-sm"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-600 mb-1 block">Status</label>
                            <Select
                                value={filterShowUndone}
                                onChange={(v) => { setFilterShowUndone(v); setPage(1); }}
                                placeholder="All statuses"
                                options={[
                                    { label: "All", value: "all" },
                                    { label: "Active (not undone)", value: "active" },
                                    { label: "Undone", value: "undone" }
                                ]}
                            />
                        </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                        <Button size="sm" onClick={() => loadActions(1)} disabled={loading}>
                            Apply Filters
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                setFilterType("all");
                                setFilterDateFrom("");
                                setFilterDateTo("");
                                setFilterShowUndone("all");
                                setPage(1);
                            }}
                        >
                            Clear
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-36">Type</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead className="w-32 text-center">Leads Affected</TableHead>
                                    <TableHead className="w-36">Performed By</TableHead>
                                    <TableHead className="w-40">Date</TableHead>
                                    <TableHead className="w-28 text-center">Status</TableHead>
                                    <TableHead className="w-32 text-center">Undone By</TableHead>
                                    <TableHead className="w-24 text-center">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-12 text-gray-400">
                                            <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
                                            Loading actions...
                                        </TableCell>
                                    </TableRow>
                                ) : actions.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-12 text-gray-400">
                                            <Activity className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                            No management actions found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    actions.map((action) => {
                                        const Icon = ACTION_ICONS[action.actionType] || Activity;
                                        const colorClass = ACTION_TYPE_COLORS[action.actionType] || "bg-gray-100 text-gray-700";
                                        const isUndoable = UNDOABLE_TYPES.includes(action.actionType) && !action.isUndone;
                                        const isUndoing = undoingId === action._id;

                                        return (
                                            <TableRow key={action._id} className={action.isUndone ? "opacity-50" : ""}>
                                                <TableCell>
                                                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
                                                        <Icon className="h-3 w-3" />
                                                        {ACTION_TYPE_LABELS[action.actionType] || action.actionType}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-sm text-gray-700 max-w-xs">
                                                    <span title={action.description}>
                                                        {action.description}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-center text-sm font-medium text-gray-800">
                                                    {action.affectedLeadCount}
                                                </TableCell>
                                                <TableCell className="text-sm text-gray-700">
                                                    {getPerformerName(action)}
                                                </TableCell>
                                                <TableCell className="text-sm text-gray-500">
                                                    {formatDate(action.createdAt)}
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    {action.isUndone ? (
                                                        <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-500">
                                                            Undone
                                                        </Badge>
                                                    ) : UNDOABLE_TYPES.includes(action.actionType) ? (
                                                        <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                                                            Active
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="secondary" className="text-xs bg-red-50 text-red-500">
                                                            Permanent
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-center text-sm text-gray-500">
                                                    {action.isUndone ? (
                                                        <span className="text-xs">
                                                            {getUndoerName(action)}
                                                            <br />
                                                            <span className="text-gray-400">{formatDate(action.undoneAt)}</span>
                                                        </span>
                                                    ) : "—"}
                                                </TableCell>
                                                <TableCell className="text-center">
                                                    {isUndoable && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-8 px-3 text-xs border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                                                            disabled={isUndoing}
                                                            onClick={() => setConfirmUndo(action)}
                                                        >
                                                            {isUndoing ? (
                                                                <RefreshCw className="h-3 w-3 animate-spin" />
                                                            ) : (
                                                                <>
                                                                    <Undo2 className="h-3 w-3 mr-1" />
                                                                    Undo
                                                                </>
                                                            )}
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t">
                            <p className="text-sm text-gray-500">
                                Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total} actions
                            </p>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page <= 1 || loading}
                                    onClick={() => { setPage(page - 1); loadActions(page - 1); }}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    Prev
                                </Button>
                                <span className="text-sm text-gray-600">
                                    Page {page} of {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page >= totalPages || loading}
                                    onClick={() => { setPage(page + 1); loadActions(page + 1); }}
                                >
                                    Next
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Undo Confirmation Dialog */}
            <Dialog open={!!confirmUndo} onOpenChange={(open) => { if (!open) setConfirmUndo(null); }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Undo2 className="h-5 w-5 text-orange-500" />
                            Confirm Undo
                        </DialogTitle>
                        <DialogDescription>
                            Are you sure you want to undo this action? This will revert all affected leads to their previous state.
                        </DialogDescription>
                    </DialogHeader>
                    {confirmUndo && (
                        <div className="space-y-3">
                            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1.5">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-600 w-24">Type:</span>
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${ACTION_TYPE_COLORS[confirmUndo.actionType]}`}>
                                        {ACTION_TYPE_LABELS[confirmUndo.actionType]}
                                    </span>
                                </div>
                                <div className="flex items-start gap-2">
                                    <span className="font-medium text-gray-600 w-24 shrink-0">Description:</span>
                                    <span className="text-gray-700">{confirmUndo.description}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-600 w-24">Leads:</span>
                                    <span className="text-gray-700">{confirmUndo.affectedLeadCount} lead(s) will be reverted</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-600 w-24">Performed:</span>
                                    <span className="text-gray-700">{formatDate(confirmUndo.createdAt)}</span>
                                </div>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-amber-800 text-sm">
                                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                                <span>
                                    This action cannot be reversed once undone. The leads will return to their previous agents, desks, and floor managers.
                                </span>
                            </div>
                            <div className="flex justify-end gap-2 pt-1">
                                <Button variant="outline" onClick={() => setConfirmUndo(null)}>
                                    Cancel
                                </Button>
                                <Button
                                    className="bg-orange-500 hover:bg-orange-600 text-white"
                                    onClick={() => handleUndo(confirmUndo)}
                                >
                                    <Undo2 className="h-4 w-4 mr-1.5" />
                                    Yes, Undo Action
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
